// Basic tracking helper — dataLayer + Meta Pixel bridge.
// All events go to window.dataLayer (ready for GTM/GA4) and, when relevant,
// are mirrored to the Meta Pixel (fbq) as standard or custom events.

// Map our internal events to Meta Pixel standard/custom events.
function emitToMetaPixel(eventName, payload) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;

  switch (eventName) {
    case "checkout_cta_click":
      // Standard event — strong signal for Meta ad optimization.
      window.fbq("track", "InitiateCheckout", {
        content_name: "Jornada do Sono",
        content_category: "online_course",
        value: 497.0,
        currency: "BRL",
        cta_id: payload.cta_id,
      });
      break;

    case "lead_submit_success":
      // Standard event — best for Lead campaigns.
      window.fbq("track", "Lead", {
        content_name: "Lead Capture WhatsApp",
        content_category: "indecisos",
      });
      break;

    case "vsl_play":
      // Standard event — VSL is the main "content" of the page.
      window.fbq("track", "ViewContent", {
        content_name: "VSL Hero — Jornada do Sono",
        content_type: "video",
      });
      break;

    case "vsl_progress":
      // Custom event — segment retention milestones.
      window.fbq("trackCustom", "VSLProgress", {
        percent: payload.percent,
        video_id: payload.video_id,
      });
      break;

    case "scroll_depth":
      window.fbq("trackCustom", "ScrollDepth", { percent: payload.percent });
      break;

    case "time_on_page":
      window.fbq("trackCustom", "TimeOnPage", { seconds: payload.seconds });
      break;

    default:
      // page_view / lead_submit_attempt / lead_submit_error → dataLayer only.
      break;
  }
}

export function track(eventName, payload = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    timestamp: new Date().toISOString(),
    ...payload,
  });

  emitToMetaPixel(eventName, payload);

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[track]", eventName, payload);
  }
}

// Track once per page-view so we don't duplicate scroll/visibility events.
const fired = new Set();
export function trackOnce(eventName, payload = {}) {
  if (fired.has(eventName)) return;
  fired.add(eventName);
  track(eventName, payload);
}

// ── Scroll depth (25/50/75/100) ─────────────────────────────────────────────
export function initScrollDepthTracking() {
  if (typeof window === "undefined") return () => {};
  const thresholds = [25, 50, 75, 100];
  const reached = new Set();

  const onScroll = () => {
    const doc = document.documentElement;
    const scrolled = window.scrollY + window.innerHeight;
    const total = doc.scrollHeight;
    if (total <= window.innerHeight) return;
    const pct = Math.min(100, Math.round((scrolled / total) * 100));
    for (const t of thresholds) {
      if (pct >= t && !reached.has(t)) {
        reached.add(t);
        track("scroll_depth", { percent: t });
      }
    }
  };

  let raf = null;
  const handler = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = null;
      onScroll();
    });
  };

  window.addEventListener("scroll", handler, { passive: true });
  onScroll();
  return () => window.removeEventListener("scroll", handler);
}

// ── Time on page (30s / 60s / 120s) ─────────────────────────────────────────
export function initTimeOnPageTracking() {
  if (typeof window === "undefined") return () => {};
  const marks = [30, 60, 120];
  const timers = marks.map((s) =>
    setTimeout(() => track("time_on_page", { seconds: s }), s * 1000)
  );
  return () => timers.forEach(clearTimeout);
}
